import { assertNever } from "@impero-com/common/utils/obj";
import { zip } from "lodash-es";
import {
    Parjser,
    ParjsResult,
    string,
    anyCharOf,
    anyStringOf,
    float,
    whitespace,
    ResultKind,
} from "parjs";
import {
    between,
    later,
    manySepBy,
    many,
    or,
    andThen,
    qthen,
    map,
} from "parjs/combinators";
import { ParjserBase, ParsingState } from "parjs/internal";

function uuid(): Parjser<string> {
    return new (class Uuid extends ParjserBase {
        type = "uuid";
        expecting = "expecting UUID";
        _apply(ps: ParsingState) {
            const { input, position } = ps;
            let end = position;
            while (end < input.length) {
                const c = input.charAt(end);
                if (
                    !(
                        (c >= "0" && c <= "9") ||
                        (c >= "a" && c <= "f") ||
                        (c >= "A" && c <= "F") ||
                        c === "-"
                    )
                ) {
                    break;
                }
                ++end;
            }
            const uuid = input.substring(position, end);
            if (
                /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                    uuid
                )
            ) {
                ps.position = end;
                ps.value = uuid.toLowerCase();
                ps.kind = ResultKind.Ok;
            } else {
                ps.kind = ResultKind.SoftFail;
            }
        }
    })();
}

const FUNCTION_ADD = "add";
const FUNCTION_SUB = "sub";
const FUNCTION_MUL = "mul";
const FUNCTION_DIV = "div";
const FUNCTION_LOG = "log";
const FUNCTION_MAX = "max";
const FUNCTION_ROUND = "round";

const FUNCTIONS_AND_NUM_ARGS: {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    [funcName: string]: [Function, number, number];
} = {
    [FUNCTION_ADD]: [(left: number, right: number) => left + right, 2, 2],
    [FUNCTION_SUB]: [(left: number, right: number) => left - right, 2, 2],
    [FUNCTION_MUL]: [(left: number, right: number) => left * right, 2, 2],
    [FUNCTION_DIV]: [(left: number, right: number) => left / right, 2, 2],

    [FUNCTION_LOG]: [Math.log, 1, 1],
    [FUNCTION_MAX]: [Math.max, 1, -1],
    [FUNCTION_ROUND]: [Math.round, 1, 1],
};

export interface NumExpr {
    type: "NUM";
    value: number;
}

export interface VarExpr {
    type: "VAR";
    uuid: string;
}

export interface ApplyExpr {
    type: "APPLY";
    func: string;
    args: Array<Expr>;
}

export type Expr = NumExpr | VarExpr | ApplyExpr;

const oneToManySepBy = (parser: Parjser<Expr>, separator: Parjser<string>) => {
    return parser.pipe(
        andThen(separator.pipe(andThen(parser), many())),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        map(([head, tail]: [Expr, any[]]) => {
            return [head].concat(tail);
        })
    );
};

const parseOpTerm = (
    parseChild: Parjser<Expr>,
    tokens: string,
    funcs: string[]
): Parjser<ApplyExpr> => {
    return oneToManySepBy(
        parseChild.pipe(between(whitespace())),
        anyCharOf(tokens)
    ).pipe(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        map((result: any[]) => {
            let res = result[0];
            let i = 1;
            while (i < result.length) {
                let func: string | undefined;
                for (const [token, f] of zip([...tokens], funcs)) {
                    if (result[i][0] === token) {
                        func = f;
                    }
                }
                if (func === undefined) {
                    throw new Error(
                        "Cannot find matching operator for " + result[i][0]
                    );
                }
                res = {
                    type: "APPLY" as const,
                    func,
                    args: [res, result[i][1]],
                };
                i++;
            }

            return res;
        })
    );
};

const parseExpression = later<Expr>();

const pLeftParen = string("(");
const pRightParen = string(")");

const parseVariable = string("$").pipe(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    qthen(uuid()),
    map((uuid) => {
        return {
            type: "VAR" as const,
            uuid,
        };
    })
);

const parseBetweenParentheses = parseExpression.pipe(
    between(pLeftParen, pRightParen),
    map((result) => result as ApplyExpr)
);

const parseFunc = anyStringOf(FUNCTION_LOG, FUNCTION_MAX, FUNCTION_ROUND).pipe(
    andThen(
        parseExpression.pipe(
            manySepBy(","),
            between(pLeftParen, pRightParen),
            map((result) => result as ApplyExpr[])
        )
    ),
    map(([funcName, args]: [string, ApplyExpr[]]) => {
        const fana = FUNCTIONS_AND_NUM_ARGS[funcName];
        if (fana === undefined) {
            throw new Error("Cannot find matching function for " + funcName);
        }
        const [, minNumArgs, maxNumArgs] = fana;
        if (
            (minNumArgs >= 0 && args.length < minNumArgs) ||
            (maxNumArgs >= 0 && args.length > maxNumArgs)
        ) {
            throw new Error(
                `Function ${funcName} takes ${
                    minNumArgs >= 0 ? minNumArgs : 0
                } to ${maxNumArgs >= 0 ? maxNumArgs : "infinite"} argument(s)`
            );
        }

        return { type: "APPLY" as const, func: funcName, args };
    })
);

const parseNumber = float().pipe(
    map((term) => ({ type: "NUM" as const, value: term }))
);

const parseFactor = parseNumber.pipe(
    or(parseVariable, parseFunc, parseBetweenParentheses)
);

const parseTerm = parseOpTerm(parseFactor, "*/", [FUNCTION_MUL, FUNCTION_DIV]);

parseExpression.init(
    parseOpTerm(parseTerm, "+-", [FUNCTION_ADD, FUNCTION_SUB])
);

export const parseFormula = (expression: string): ParjsResult<Expr> => {
    return parseExpression.parse(expression);
};

export const printFormula = (expression: string): string => {
    const astExpression = parseFormula(expression);
    return astExpression.kind === "OK"
        ? astToPrefixedNotation(astExpression.value)
        : astExpression.kind;
};

export const allVariables = (expression: Expr): Set<VarExpr> => {
    return getVariablesSet(expression, new Set());
};

export const getVariablesSet = (
    expression: Expr,
    variables: Set<VarExpr>
): Set<VarExpr> => {
    switch (expression.type) {
        case "APPLY":
            expression.args.map((arg) => getVariablesSet(arg, variables));
            break;
        case "VAR":
            variables.add(expression);
            break;
        case "NUM":
            break;
        default:
            assertNever(expression);
    }

    return variables;
};

export const astToPrefixedNotation = (astExpression: Expr): string => {
    switch (astExpression.type) {
        case "APPLY":
            return `(${astExpression.func} ${astExpression.args
                .map((arg) => astToPrefixedNotation(arg))
                .join(" ")})`;
        case "VAR":
            return `\$${astExpression.uuid}`;
        case "NUM":
            return astExpression.value.toString();
        default:
            return assertNever(astExpression);
    }
};

export const evalAst = (
    astExpression: Expr,
    getUuidValue: (uuid: string) => number
): any => {
    switch (astExpression.type) {
        case "APPLY":
            const [func] = FUNCTIONS_AND_NUM_ARGS[astExpression.func];

            return func(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                ...astExpression.args.map((ast) => evalAst(ast, getUuidValue))
            );
        case "NUM":
            return astExpression.value;
        case "VAR":
            return getUuidValue(astExpression.uuid);
        default:
            assertNever(astExpression);
    }
};
